import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, RefreshCw, UserPlus, CheckCircle2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Organizer view: Claude-picked vendor shortlist for THIS event, each with a
 * short reasoning + one-tap "Invite" that fires an in-app notification to the
 * vendor (via /api/ai/invite-vendor). Cached 24h server-side.
 */
export default function AISuggestedVendors({ eventId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [inviting, setInviting] = useState({}); // {vendorId: true}
  const [invited, setInvited] = useState({});
  const [err, setErr] = useState("");

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await api.get(`/ai/vendors-for-event/${eventId}?limit=6${refresh ? "&refresh=true" : ""}`);
      setData(data);
      setErr("");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Could not load AI suggestions";
      setErr(msg);
      if (refresh) toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [eventId]);

  const invite = async (vendorId, name) => {
    setInviting((s) => ({ ...s, [vendorId]: true }));
    try {
      const { data } = await api.post("/ai/invite-vendor", { event_id: eventId, vendor_id: vendorId });
      setInvited((s) => ({ ...s, [vendorId]: true }));
      if (data?.already_invited) toast.info(`${name} was already invited.`);
      else toast.success(`Invite sent to ${name}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invite failed");
    } finally {
      setInviting((s) => ({ ...s, [vendorId]: false }));
    }
  };

  const matches = data?.matches || [];
  const strong = data?.strong_threshold ?? 78;
  const good = data?.good_threshold ?? 60;

  return (
    <div className="mt-8" data-testid="ai-suggested-vendors">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--gold)" }}>
            <Sparkles className="h-3 w-3" /> AI shortlist
          </div>
          <div className="font-serif text-3xl mt-1">Vendors who&apos;d fit this event.</div>
          <p className="mt-1 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
            Our AI scanned every vendor on AddVendTour and picked the strongest matches. Tap Invite to nudge them.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing || loading}
          data-testid="ai-suggested-refresh"
          className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border"
          style={{ borderColor: "rgba(0,0,0,0.15)", backgroundColor: "#fff", color: "var(--ink)" }}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Thinking…" : "Refresh"}
        </button>
      </div>

      {loading && !data && (
        <div
          data-testid="ai-suggested-loading"
          className="mt-6 rounded-lg border-2 border-dashed p-8 text-sm text-center"
          style={{ borderColor: "rgba(0,0,0,0.06)", color: "var(--ink-soft)" }}
        >
          <Sparkles className="mx-auto h-5 w-5 animate-pulse" style={{ color: "var(--gold)" }} />
          <div className="mt-3">Reading every vendor profile for the strongest matches…</div>
        </div>
      )}

      {err && !loading && (
        <div
          data-testid="ai-suggested-error"
          className="mt-6 text-sm rounded-md p-3"
          style={{ backgroundColor: "rgba(212,88,58,0.10)", color: "var(--terracotta)" }}
        >
          {err}
        </div>
      )}

      {!loading && !err && matches.length === 0 && (
        <div
          data-testid="ai-suggested-empty"
          className="mt-6 rounded-lg border-2 border-dashed p-8 text-center text-sm"
          style={{ borderColor: "rgba(0,0,0,0.08)", color: "var(--ink-soft)" }}
        >
          No vendors match this event yet — try adding more detail to the event description.
        </div>
      )}

      {matches.length > 0 && (
        <ul data-testid="ai-suggested-list" className="mt-6 space-y-3">
          {matches.map((m) => {
            const band = m.score >= strong ? "strong" : m.score >= good ? "good" : "stretch";
            const bandStyles = {
              strong: { bg: "rgba(31,93,52,0.10)", color: "var(--forest)", label: "Strong fit" },
              good: { bg: "rgba(201,162,75,0.14)", color: "#a67c1e", label: "Good fit" },
              stretch: { bg: "rgba(0,0,0,0.05)", color: "var(--ink-soft)", label: "Stretch" },
            }[band];
            const already = invited[m.vendor_id];
            const busy = inviting[m.vendor_id];
            return (
              <li
                key={m.vendor_id}
                data-testid={`ai-suggested-${m.vendor_id}`}
                className="card-warm rounded-lg p-4 flex items-start justify-between gap-4 flex-wrap"
                style={{ backgroundColor: "#fff", borderLeft: `3px solid ${bandStyles.color}` }}
              >
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      to={m.slug ? `/vendors/${m.slug}` : "#"}
                      className="font-serif text-lg hover:underline"
                    >
                      {m.name}
                    </Link>
                    {m.verified && (
                      <span className="text-xs inline-flex items-center gap-1" style={{ color: "var(--moss)" }}>
                        <CheckCircle2 className="h-3 w-3" /> verified
                      </span>
                    )}
                    <span
                      className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: bandStyles.bg, color: bandStyles.color }}
                    >
                      {bandStyles.label} · {m.score}
                    </span>
                  </div>
                  <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                    {m.categories?.slice(0, 3).join(" · ") || "—"} · {m.location || "no location"}
                    {m.rating_count > 0 && ` · ★ ${m.rating_avg} (${m.rating_count})`}
                  </div>
                  <p className="mt-2 text-sm italic" style={{ color: "var(--ink-soft)" }}>
                    “{m.reasoning}”
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => invite(m.vendor_id, m.name)}
                  disabled={busy || already}
                  data-testid={`ai-invite-${m.vendor_id}`}
                  className="btn-outline rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5 whitespace-nowrap"
                  style={already ? { backgroundColor: "rgba(31,93,52,0.10)", color: "var(--forest)", borderColor: "transparent" } : {}}
                >
                  {already ? <><Check className="h-3.5 w-3.5" /> Invited</> : busy ? "Sending…" : <><UserPlus className="h-3.5 w-3.5" /> Invite</>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
